import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { calculerRepartition, decrireRegle } from '../../analysis/repartition'
import { enregistrerModele, supprimerModele } from '../../db/repo'
import type { Cents } from '../../domain/types'
import { Carte, Champ, ChampMontant, Confirmation, Encart, Entete, IconeChevron, IconePlus, useAction } from '../composants'
import { useDonnees } from '../donnees'
import { ApercuCascade, EditeurParts, cle, nouvellePart, versCalculables, type PartEdition } from '../EditeurParts'

export function PageModeles() {
  const { d, ix } = useDonnees()
  const navigate = useNavigate()
  return (
    <>
      <Entete titre="Modèles de répartition" retour />
      <main className="contenu">
        <Encart>
          Un modèle enregistre une cascade qui revient souvent — « caisse maladie puis moitié-moitié avec l’ex-épouse ». On l’applique en
          un geste à la saisie, ou on le propose par défaut sur une catégorie. L’appliquer <strong>copie</strong> ses parts : le modifier
          ensuite ne change aucune dépense déjà enregistrée.
        </Encart>
        {d.modeles.length === 0 ? (
          <p className="vide">Aucun modèle. Créez-en un ici, ou depuis une dépense déjà répartie (« Enregistrer comme modèle »).</p>
        ) : (
          <ul className="liste">
            {d.modeles.map((m) => (
              <li key={m.id}>
                <Link to={`/modeles/${m.id}`} className="item">
                  <div className="grandir">
                    <div className="item__principal">{m.nom}</div>
                    <div className="item__secondaire">
                      {m.parts.map((p) => `${ix.tiersParId.get(p.tiersId)?.nom ?? '?'} (${decrireRegle(p)})`).join(' → ')}
                    </div>
                  </div>
                  <span className="chevron"><IconeChevron /></span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
      <button type="button" className="fab" aria-label="Nouveau modèle" onClick={() => navigate('/modeles/nouveau')}>
        <IconePlus />
      </button>
    </>
  )
}

export function PageModele() {
  const { id } = useParams()
  const { d } = useDonnees()
  const navigate = useNavigate()
  const existant = d.modeles.find((m) => m.id === id)

  const [nom, setNom] = useState(existant?.nom ?? '')
  const [parts, setParts] = useState<PartEdition[]>(() => (existant?.parts ?? []).map((p) => ({ ...p, cle: cle() })))
  const [exemple, setExemple] = useState<Cents | null>(40000)
  const [confirmer, setConfirmer] = useState(false)
  const { erreur, executer } = useAction()

  const apercu = useMemo(() => calculerRepartition(exemple ?? 0, versCalculables(parts), d.moiId), [exemple, parts, d.moiId])
  const tiersAutres = d.tiers.filter((t) => t.type !== 'moi' && !t.archive)

  const enregistrer = () =>
    executer(async () => {
      if (!nom.trim()) throw new Error('Donnez un nom au modèle.')
      if (parts.length === 0) throw new Error('Ajoutez au moins une part.')
      if (parts.some((p) => !p.tiersId)) throw new Error('Chaque part doit désigner un tiers.')
      await enregistrerModele({ id: existant?.id, nom: nom.trim(), parts: parts.map(({ cle: _c, id: _i, ...p }, i) => ({ ...p, ordre: i + 1 })) })
      navigate(-1)
    })

  return (
    <>
      <Entete titre={existant ? existant.nom : 'Nouveau modèle'} retour />
      <main className="contenu">
        <Carte>
          <div className="pile">
            <Champ libelle="Nom">
              <input type="text" value={nom} placeholder="Santé enfants — caisse puis 50/50" onChange={(e) => setNom(e.target.value)} />
            </Champ>
            <EditeurParts parts={parts} onChange={(f) => setParts(f)} />
            <button type="button" className="btn btn--petit" onClick={() => setParts((l) => [...l, nouvellePart(tiersAutres[0]?.id ?? '', l.length + 1)])}>
              <IconePlus /> Ajouter une part
            </button>
            <p className="tres-doux">
              Un montant fixe est rarement pertinent dans un modèle, sauf pour un forfait constant : le remboursement d’une assurance varie
              d’une facture à l’autre et se corrige à la saisie.
            </p>
          </div>
        </Carte>

        {parts.length > 0 && (
          <Carte titre="Essai">
            <div className="pile">
              <Champ libelle="Pour une facture de">
                <ChampMontant valeur={exemple} onChange={setExemple} />
              </Champ>
              {exemple !== null && exemple > 0 && <ApercuCascade apercu={apercu} />}
            </div>
          </Carte>
        )}

        {erreur && <Encart ton="danger">{erreur}</Encart>}
        <button type="button" className="btn btn--principal btn--bloc" onClick={() => void enregistrer()}>Enregistrer</button>
        {existant && <button type="button" className="btn btn--danger btn--bloc" onClick={() => setConfirmer(true)}>Supprimer</button>}
      </main>
      {confirmer && existant && (
        <Confirmation
          titre="Supprimer ce modèle ?"
          message="Les dépenses déjà réparties gardent leur répartition. Les catégories et récurrences qui le proposaient n’en proposeront plus."
          libelleConfirmer="Supprimer"
          danger
          onAnnuler={() => setConfirmer(false)}
          onConfirmer={() => void supprimerModele(existant.id).then(() => navigate('/modeles', { replace: true }))}
        />
      )}
    </>
  )
}
